import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import MovieGrid from '../../../components/ui/MovieGrid';
import { ItemListProduct } from '../../../types/post.type';
import { Loader } from 'lucide-react';
import GridSection from '../../../components/ui/sections/GridSection';
import NotFound from '../../NotFound';
import NotFoundList from '../../../components/ui/NotFoundList';

interface SearchResultsProps {
  results: ItemListProduct[];
  isLoading: boolean;
  filters: string[];
  selectedFilter: string;
  onFilterChange: (filter: string) => void;
  loadMore: () => void;
  isTyping?: boolean;
  // 
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
} 

const SearchResults: React.FC<SearchResultsProps> = ({
  results,
  isLoading,
  filters,
  selectedFilter,
  onFilterChange,
  loadMore,
  isTyping = false,
  // 
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}) => {
  const { t } = useTranslation();

   useEffect(() => {
      // Track whether a fetch is already in progress to prevent duplicate calls
      let isFetching = false;
      
      const onScroll = () => {
        // Calculate distance from bottom
        const scrollPosition = window.innerHeight + document.documentElement.scrollTop;
        const scrollThreshold = document.documentElement.offsetHeight - 200; // 200px before the bottom
        
        // Check if we're near the bottom and can fetch more
        if (
          scrollPosition >= scrollThreshold &&
          hasNextPage && 
          !isFetchingNextPage &&
          !isFetching
        ) {
          console.log("Reached scroll threshold, fetching next page");
          isFetching = true; // Prevent multiple calls while scroll event fires
          
          // Fetch next page
          fetchNextPage();
          
          // Reset after a short delay to ensure the operation completes
          setTimeout(() => {
            isFetching = false;
          }, 300);
        }
      };
      
      // Debounce scroll handler to improve performance
      let scrollTimeout: ReturnType<typeof setTimeout>;
      const debouncedOnScroll = () => {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(onScroll, 100);
      };
      
      window.addEventListener("scroll", debouncedOnScroll);
      
      // Clean up
      return () => {
        window.removeEventListener("scroll", debouncedOnScroll);
        clearTimeout(scrollTimeout);
      };
    }, [fetchNextPage, hasNextPage, isFetchingNextPage]);
  
  // Show loading spinner when data is being fetched (initial search only)
  if (isLoading && results.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 min-h-[60vh]">
        <Loader className="h-10 w-10 text-blue-500 animate-spin" />
        <p className="mt-4 text-gray-400">
          {isTyping ? t('search.typing') || 'Typing...' : t('search.loading') || 'Loading results...'}
        </p>
      </div>
    );
  }

  // Show message when no results are found
  if (results.length === 0 && !isLoading) {
    return (
      <NotFoundList 
        title={t('search.notFoundSearch') || 'No results found'}
        description={t('search.description_search') || 'Try different keywords or filters'}
         />
      // <div className="flex flex-col items-center justify-center py-16 px-4 min-h-[60vh]">
      //   <div className="h-24 w-24 text-gray-500 mb-4">
      //     <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      //       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      //     </svg>
      //   </div>
      //   <p className="text-xl text-gray-400 mb-2">{t('search.noResults') || 'No results found'}</p>
      //   <p className="text-gray-500 text-center">{t('search.tryDifferent') || 'Try different keywords or filters'}</p>
      // </div>
    );
  }

  return (
    <div className="mt-[20vh]">
      <GridSection 
        movies={results} 
      />
      {/* Loading more indicator for loadmore (infinite scroll) */}
      {isFetchingNextPage && results.length > 0 && (
        <div className="p-4 text-center flex flex-col items-center justify-center transition-all duration-300">
          <div className="w-8 h-8 border-t-2 border-b-2 border-blue-500 rounded-full animate-spin mb-2"></div>
          <p>Đang tải thêm...</p>
        </div>
      )}
    </div>
  );
};

export default SearchResults;
